import { useState } from 'react';
import { Bot, HelpCircle, Search, Sparkles, TrendingUp } from 'lucide-react';

import {
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorAlert,
  Input,
  PageHeader,
  Skeleton,
  StatCard,
  Table,
  Td,
  Th,
} from '@/components/ui';
import { statusTone } from '@/lib/status';
import {
  readProductRefs,
  useAssistantSummary,
  useChatSessions,
  useChatTranscript,
  useUnansweredQuestions,
} from '@/lib/assistant-api';
import { cn, errorMessage, formatDateTime, formatNumber } from '@/lib/utils';

/**
 * AI assistant transcripts and reporting (spec §20, §63).
 *
 * Two questions this screen exists to answer: what is the assistant telling
 * buyers, and what can it not answer. The second is the actionable one — an
 * unanswered question is either a gap in the catalogue data or a gap in the
 * system prompt, and both are fixable from elsewhere in this panel.
 *
 * Read-only by design: a transcript is evidence of what a buyer was told, and
 * an editable one would be worth nothing.
 */

type Tab = 'sessions' | 'unanswered';

export function AssistantPage() {
  const [tab, setTab] = useState<Tab>('sessions');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const summary = useAssistantSummary();
  const sessions = useChatSessions({ page, limit: 20, search });
  const unanswered = useUnansweredQuestions({ page, limit: 20 });

  const active = tab === 'sessions' ? sessions : unanswered;

  return (
    <div>
      <PageHeader
        title="AI Assistant"
        description="What the assistant is telling buyers, and what it cannot answer."
      />

      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summary.isLoading ? (
          [0, 1, 2, 3].map((index) => <Skeleton key={index} className="h-24" />)
        ) : (
          <>
            <StatCard
              label="Conversations"
              value={formatNumber(summary.data?.sessions ?? 0)}
              icon={<Bot className="size-5" aria-hidden="true" />}
              tone="brand"
            />
            <StatCard
              label="Answer rate"
              value={`${summary.data?.answerRatePercent ?? 0}%`}
              icon={<TrendingUp className="size-5" aria-hidden="true" />}
              tone="success"
            />
            <StatCard
              label="Could not answer"
              value={formatNumber(summary.data?.unanswered ?? 0)}
              icon={<HelpCircle className="size-5" aria-hidden="true" />}
              tone="warning"
            />
            <StatCard
              label="Leads it captured"
              value={formatNumber(summary.data?.aiInquiries ?? 0)}
              icon={<Sparkles className="size-5" aria-hidden="true" />}
              tone="success"
            />
          </>
        )}
      </div>

      <Card className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {tab === 'sessions' && (
            <div className="relative min-w-56 flex-1">
              <Search
                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
                aria-hidden="true"
              />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder="Search session key or company"
                className="pl-9"
                aria-label="Search conversations"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Assistant view">
            {(
              [
                ['sessions', 'Conversations'],
                ['unanswered', 'Unanswered questions'],
              ] as Array<[Tab, string]>
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={tab === value}
                onClick={() => {
                  setTab(value);
                  setPage(1);
                }}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
                  tab === value
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-muted hover:bg-canvas hover:text-ink',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      <Card>
        {active.isLoading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        ) : active.isError ? (
          <div className="p-4">
            <ErrorAlert title="Could not load this">{errorMessage(active.error)}</ErrorAlert>
          </div>
        ) : tab === 'sessions' ? (
          (sessions.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              title="No conversations yet"
              description="Chats with the storefront assistant will appear here."
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Conversation</Th>
                  <Th>Customer</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Messages</Th>
                  <Th className="text-right">Leads</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {sessions.data?.items.map((session) => (
                  <tr key={session.id} className="hover:bg-canvas/60">
                    <Td>
                      <p className="font-mono text-xs text-ink">{session.sessionKey}</p>
                      <p className="text-xs text-ink-subtle">{formatDateTime(session.startedAt)}</p>
                    </Td>
                    <Td>
                      {session.customer ? (
                        <>
                          <p className="text-ink">{session.customer.companyName}</p>
                          <p className="text-xs text-ink-subtle">{session.customer.customerCode}</p>
                        </>
                      ) : (
                        <span className="text-sm text-ink-subtle">Anonymous visitor</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={statusTone(session.status)}>{session.status}</Badge>
                    </Td>
                    <Td className="text-right tabular-nums">{session.messageCount}</Td>
                    <Td className="text-right tabular-nums">
                      {session.inquiryCount > 0 ? (
                        <span className="font-medium text-success-700">{session.inquiryCount}</span>
                      ) : (
                        <span className="text-ink-subtle">—</span>
                      )}
                    </Td>
                    <Td className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setSelectedId(session.id)}>
                        Transcript
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )
        ) : (unanswered.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            title="Nothing unanswered"
            description="Every question the assistant was asked, it answered from the catalogue."
          />
        ) : (
          <div className="divide-y divide-line">
            <p className="px-4 py-3 text-sm text-ink-muted">
              Each of these is either missing catalogue data or a gap in the system prompt — both
              fixable from this panel.
            </p>
            {unanswered.data?.items.map((row) => (
              <div key={row.id} className="flex items-start justify-between gap-4 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-ink">{row.question ?? '—'}</p>
                  <p className="mt-1 text-xs text-ink-subtle">{formatDateTime(row.createdAt)}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setSelectedId(row.sessionId)}>
                  Transcript
                </Button>
              </div>
            ))}
          </div>
        )}

        {active.data && active.data.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-3">
            <p className="text-sm text-ink-subtle">
              {active.data.pagination.total} rows · page {active.data.pagination.page} of{' '}
              {active.data.pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!active.data.pagination.hasPreviousPage}
                onClick={() => setPage((current) => current - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!active.data.pagination.hasNextPage}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      <TranscriptDrawer sessionId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

/* ==========================================================================
   Transcript
   ========================================================================== */

function TranscriptDrawer({
  sessionId,
  onClose,
}: {
  sessionId: string | null;
  onClose: () => void;
}) {
  const transcript = useChatTranscript(sessionId);
  const session = transcript.data;

  return (
    <Drawer open={Boolean(sessionId)} onClose={onClose} title="Conversation">
      {transcript.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-40" />
        </div>
      ) : transcript.isError ? (
        <ErrorAlert title="Could not load the transcript">
          {errorMessage(transcript.error)}
        </ErrorAlert>
      ) : (
        session && (
          <div className="space-y-5">
            <section>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <Badge tone={statusTone(session.status)}>{session.status}</Badge>
                {session.escalatedAt && <Badge tone="warning">Escalated</Badge>}
              </div>
              <p className="text-sm text-ink">
                {session.customer?.companyName ?? 'Anonymous visitor'}
              </p>
              <p className="text-xs text-ink-subtle">
                {session.customer?.customerCode ?? session.sessionKey} ·{' '}
                {formatDateTime(session.startedAt)}
              </p>
            </section>

            {session.inquiries.length > 0 && (
              <section className="rounded-lg bg-success-50 p-3">
                <p className="text-sm font-medium text-success-700">
                  This conversation produced a lead
                </p>
                {session.inquiries.map((inquiry) => (
                  <p key={inquiry.id} className="text-xs text-success-700/90">
                    {inquiry.inquiryNumber} · {inquiry.status}
                  </p>
                ))}
              </section>
            )}

            <section>
              <h3 className="mb-2 text-sm font-semibold text-ink">Transcript</h3>
              <div className="space-y-2">
                {session.messages
                  .filter((message) => message.role !== 'SYSTEM')
                  .map((message) => {
                    const refs = readProductRefs(message.productRefs);

                    return (
                      <div
                        key={message.id}
                        className={cn(
                          'rounded-lg border p-3 text-sm',
                          message.role === 'USER'
                            ? 'border-line bg-canvas'
                            : message.wasAnswered === false
                              ? 'border-warning-500/40 bg-warning-50'
                              : 'border-line',
                        )}
                      >
                        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-subtle">
                          {message.role === 'USER' ? 'Buyer' : 'Assistant'}
                          {message.wasAnswered === false && ' · could not answer'}
                        </p>
                        <p className="whitespace-pre-wrap text-ink">{message.content}</p>

                        {refs.length > 0 && (
                          <p className="mt-2 text-xs text-ink-subtle">
                            Cited: {refs.join(', ')}
                          </p>
                        )}

                        {message.tokensUsed !== null && (
                          <p className="mt-1 text-xs text-ink-subtle">
                            {message.model} · {formatNumber(message.tokensUsed)} tokens
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </section>

            {session.feedback.length > 0 && (
              <section>
                <h3 className="mb-2 text-sm font-semibold text-ink">Feedback</h3>
                {session.feedback.map((entry) => (
                  <p key={entry.id} className="text-sm text-ink-muted">
                    {entry.rating >= 4 ? 'Helpful' : 'Not helpful'}
                    {entry.comment && ` — ${entry.comment}`}
                  </p>
                ))}
              </section>
            )}
          </div>
        )
      )}
    </Drawer>
  );
}
